class MainProduct extends HTMLElement {
  selectors = {
    header: '.header',
    productId: '[name="id"]',
    productTitle: '.product__title',
    productJSON: '[data-product-json]',
    productFeaturedImage: '.product__image',
    thumbnailContainer: '.product__thumbnail-images',
    thumbnail: '.product__thumbnail-images img',
    thumbnailSlider: '.product__thumbnail-images .swiper-container',
    thumbnailSliderWrapper: '.product__thumbnail-images .swiper-wrapper',
    thumbnailSlide: '.product__thumbnail-images .swiper-slide',
    thumbnailPrev: '.product__thumbnail-images .slider-button-prev',
    thumbnailNext: '.product__thumbnail-images .slider-button-next',
    thumbnailDots: '.product__thumbnail-images .dots',
    thumbnailDot: '.product__thumbnail-images .dots .swiper-pagination-bullet',
    thumbnailScrollbar: '.product__thumbnail-images .slider-scrollbar',
    productPlugSKUCartNote: '#propPlugCartNote',
    propPlug: '#propPlug',
    voltageSelect: '.option-voltage-select',
    productNavContainer: '.product__nav-container',
    productNavLink: '.product__nav a',
    productBullets: '.product__details-meta',
    productDescription: '.product__description-inner',
    productDefaultDescription: '.product__default-description-inner',
    productPurchase: '.product__purchase',
    productPrice: '.product__price',
    productSubscriptionPrice: '.product__subscription-price',
    comparePrice: '.product__compare-price',
    productDiscount: '.product__discount',
    addToCart: '.product__atc',
    addToCartText: '.product__atc span',
    buyNowButton: '.product__purchase [name="buy_now"]',
    productShipsFrom: '.product__ships-from',
    comingSoon: '.product__coming-soon',
    plugList: '.product-plug ul',
    fastFreeShipping: '.product__fast-free-shipping',
    productNotify: '.product__notify',
    linkNotify: '.product a[href="#notify-me"]',
    productReturnsPolicyBtn: '.product__returns-policy-btn',
    productReturnsPolicyContent: '#product__returns-policy-content',
    paywhirlPlanSelector: 'fieldset.paywhirl-plan-selector',
    productBadgeSale: '.breadcrumb .product__badge--sale',
  };
  #discount = null;
  #currentVariant = null;
  #cachedVariants = null;

  connectedCallback() {
    const productJSONHTML = this.querySelector(this.selectors.productJSON);
    this.product = productJSONHTML ? JSON.parse(productJSONHTML.innerHTML) : null;

    if (!this.product) {
      return;
    }

    this.variantSelect = this.querySelector(this.selectors.productId);
    this.allImages = this.querySelectorAll(this.selectors.thumbnailSlide);

    if (this.product && this.variantSelect) {
      this.currentVariant = this.variants.find(variant => variant.id === +this.variantSelect.value);
    }

    Shopify.discount.onUpdated(() => {
      this.#cachedVariants = null;
    });

    this.setupEventListeners();
    this.productNavSticky();
    this.productNavActiveTab();
    this.productNotifyObserver();

    onLibReady(['js-image-zoom', 'swiper'], () => {
      this.initSlider();
      this.initZoom();
    });
  }

  get variants() {
    if (this.#cachedVariants) {
      return this.#cachedVariants;
    }

    const discountForOneTimePurchase = Shopify.discount.appliedDiscountCodes.find(code =>
      Shopify.discount.isProductEligibleForDiscount(this.product, code, false),
    );

    const discountForSubscription = Shopify.discount.appliedDiscountCodes.find(code =>
      Shopify.discount.isProductEligibleForDiscount(this.product, code, true),
    );

    this.#cachedVariants = this.product.variants.map(variant => {
      const originalPrice = variant.price;
      const subscriptionOriginalPrice =
        variant.selling_plan_allocations?.find(plan => plan.selling_plan_id === this.sellingPlanId)?.price ??
        variant.selling_plan_allocations?.[0]?.price ??
        originalPrice;

      let newPrice = originalPrice;
      let subscriptionPrice = subscriptionOriginalPrice;

      if (discountForOneTimePurchase) {
        newPrice = Shopify.discount.getPriceWithDiscount(originalPrice, discountForOneTimePurchase);
      }

      if (discountForSubscription) {
        subscriptionPrice = Shopify.discount.getPriceWithDiscount(subscriptionOriginalPrice, discountForSubscription);
      }

      return {
        ...variant,
        price: newPrice,
        subscription_price: subscriptionPrice,
        compare_at_price: Math.max(variant.compare_at_price, originalPrice),
      };
    });

    return this.#cachedVariants;
  }

  get currentVariant() {
    if (!this.#currentVariant) return null;
    return this.variants.find(variant => variant.id === this.#currentVariant.id);
  }

  set currentVariant(value) {
    this.#currentVariant = value;
    this.render();

    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent('product.variant.change', {
          detail: {
            variantId: value.id,
          },
        }),
      );
    });
  }

  get discount() {
    return this.#discount;
  }

  setupEventListeners() {
    const linkNotify = this.querySelector(this.selectors.linkNotify);

    if (linkNotify) {
      linkNotify.addEventListener('click', event => {
        event.preventDefault();
        const productNotifyDiv = this.querySelector(this.selectors.productNotify);

        if (productNotifyDiv) {
          productNotifyDiv.style.display = 'flex';
        }
      });
    }

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', event => {
        this.currentVariant = this.variants.find(variant => variant.id === +event.target.value);
      });
    }

    window.addEventListener('resize', this.resetZoom.bind(this));
    window.addEventListener('scroll', this.changePurchaseStyle.bind(this));
    document.addEventListener('updatePlug', this.updateProductProps.bind(this));

    this.addEventListener('click', event => {
      if (event.target.closest(this.selectors.thumbnail)) {
        const src = event.target.getAttribute('srcset');
        const imageId = event.target.dataset.imageId;
        const isVideo = !!event.target.dataset.externalId;

        if (window.innerWidth > 750) {
          this.changeImage(src, imageId, isVideo);
        } else {
          this.openGallery(imageId);
        }
      }

      if (event.target.closest('.product__image')) {
        const imageId = event.target.closest('.product__image').querySelector('img').dataset.imageId;
        this.openGallery(imageId);
      }
    });

    const productReturnsPolicyBtn = this.querySelector(this.selectors.productReturnsPolicyBtn);
    if (productReturnsPolicyBtn) {
      productReturnsPolicyBtn.addEventListener('click', evt => {
        evt.preventDefault();

        if (!Fancybox) return;

        Fancybox.show(
          [
            {
              src: this.selectors.productReturnsPolicyContent,
              type: 'inline',
            },
          ],
          {
            mainClass: 'product__returns-policy-modal',
            on: {
              done: () => {
                const modalInner = document.querySelector('.product__returns-policy-modal .fancybox__track');
                const closeModalBtn = document.querySelector(
                  `${this.selectors.productReturnsPolicyContent} .carousel__button`,
                );
                if (modalInner && closeModalBtn) {
                  modalInner.prepend(closeModalBtn);
                }
              },
            },
          },
        );
      });
    }

    Shopify.discount.onUpdated(this.render.bind(this));

    const inputSellingPlan = this.querySelector('input[name="selling_plan"]');
    if (inputSellingPlan) {
      const observer = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            this.sellingPlanId = +inputSellingPlan.value;
            this.render();
            document.dispatchEvent(new CustomEvent('sellingPlan:update'));
          }
        });
      });

      observer.observe(inputSellingPlan, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
  }

  render() {
    if (this.currentVariant) {
      this.updateUrlParams();
      this.updateTitle();
      this.updatePrice();
      this.updateOptions();
      this.updateBullets();
      this.updateVoltage();
      this.updateAttr();
      this.updatePlugList();
      this.changeProductDescriptionAndBadges();
      this.variantSpecInit();
      this.updateProductProps();
      this.updateImagesList();
    }

    this.updateAvailable();
    this.updateProductGiftPage();
    this.updateProductDropPage();
  }

  updateUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    params.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}${hash}`);
  }

  updateTitle() {
    const title = this.querySelector(this.selectors.productTitle);
    if (!title) return;
    title.innerHTML = this.currentVariant.full_title;
  }

  getTagDiscount() {
    const saleTags = this.product.tags.filter(tag => tag.includes('sale_'));
    return saleTags.length ? saleTags[0].replace('sale_', '') : null;
  }

  updatePrice() {
    const priceDiv = this.querySelector(this.selectors.productPrice);
    const subscriptionPriceDiv = this.querySelector(this.selectors.productSubscriptionPrice);
    const comparePriceDiv = this.querySelector(this.selectors.comparePrice);
    const productDiscountDiv = this.querySelector(this.selectors.productDiscount);
    const productBadgeSaleDiv = document.querySelector(this.selectors.productBadgeSale);

    const { price, compare_at_price: compareAtPrice, subscription_price: subscriptionPrice } = this.currentVariant;

    if (priceDiv) {
      priceDiv.innerHTML = formatMoney(price);
    }

    if (subscriptionPriceDiv) {
      subscriptionPriceDiv.innerHTML = formatMoney(subscriptionPrice);
    }

    if (comparePriceDiv) {
      if (compareAtPrice > price) {
        comparePriceDiv.innerHTML = formatMoney(compareAtPrice);
        comparePriceDiv.style.display = 'block';
      } else {
        comparePriceDiv.innerHTML = '';
        comparePriceDiv.style.display = 'none';
      }
    }

    const discountPercentage =
      compareAtPrice > price
        ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice)
        : this.getTagDiscount() || 0;

    if (productDiscountDiv && productBadgeSaleDiv) {
      if (discountPercentage > 0) {
        productDiscountDiv.innerHTML = `<div class="discount-badge"><div>${discountPercentage}% <span>OFF</span></div></div>`;
        productDiscountDiv.style.display = 'block';
        productBadgeSaleDiv.style.display = 'block';
      } else {
        productDiscountDiv.innerHTML = '';
        productDiscountDiv.style.display = 'none';
        productBadgeSaleDiv.style.display = 'none';
      }
    }
  }

  updateOptions() {
    const swatchTypeCard = this.querySelector("[data-swatch-type='card']");

    if (!swatchTypeCard) return;

    const swatchTypeCardIndex = Number(swatchTypeCard.getAttribute('data-option-index'));

    const variants = this.variants.filter(({ options }) =>
      this.currentVariant.options.every((option, index) => index === swatchTypeCardIndex || options.includes(option)),
    );

    variants.forEach(variant => {
      const image = `${variant.featured_image}${variant.featured_image.includes('?') ? '&' : '?'}&width=60`;

      // this.querySelector(`[data-value="${variant.options[swatchTypeCardIndex].replace(/\"/g, '')}"] .money`).innerHTML =
      //   formatMoney(variant.price);
      this.querySelector(
        `[data-value="${variant.options[swatchTypeCardIndex].replace(/\"/g, '')}"] .swatch-variant-img`,
      )?.setAttribute('srcset', image);
    });
  }

  updateBullets() {
    const productBullets = this.querySelector(this.selectors.productBullets);
    if (!productBullets) return;
    productBullets.innerHTML = this.currentVariant.bullets;
  }

  updateVoltage() {
    const voltageSelect = this.querySelector(this.selectors.voltageSelect);
    if (!voltageSelect) return;
    voltageSelect.dataset.sku = this.currentVariant.sku;

    voltageSelect.querySelector('.option-voltage-select-v').innerHTML = this.querySelector('.voltage :checked').value;
  }

  updateAttr() {
    const comingSoonDiv = this.querySelector(this.selectors.comingSoon);
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);
    const productNotifyDiv = this.querySelector(this.selectors.productNotify);
    const paywhirlPlanSelectorDiv = this.querySelector(this.selectors.paywhirlPlanSelector);

    const usuallyShips = getUsuallyShips(this.currentVariant);
    const shipsFrom = getShipsFrom(this.currentVariant);
    const isShipsFromChina = shipsFrom ? shipsFrom.toLowerCase().includes('china') : false;
    const isPreOrder = this.currentVariant.inventory_management !== null && this.currentVariant.inventory_policy === 'continue' && this.currentVariant.inventory_quantity < 1;

    if (isShipsFromChina || isPreOrder) {
      fastFreeShippingDiv.style.display = 'none';
    } else {
      fastFreeShippingDiv.style.display = 'block';
    }

    if (usuallyShips) {
      productShipsFromDiv.innerHTML = usuallyShips;
      productShipsFromDiv.style.display = 'block';
    } else {
      productShipsFromDiv.style.display = 'none';
    }

    if (!isShipsFromChina && isPreOrder) {
      // comingSoonDiv.style.display = 'block';
      // fastFreeShippingDiv.style.display = 'none';

      if (paywhirlPlanSelectorDiv) {
        paywhirlPlanSelectorDiv.style.display = 'none';
      }
    } else {
      if (paywhirlPlanSelectorDiv) {
        paywhirlPlanSelectorDiv.style.display = 'block';
      }

      comingSoonDiv.style.display = 'none';
      productNotifyDiv.style.display = 'none';
    }
  }

  updateAvailable() {
    const atcButton = this.querySelector(this.selectors.addToCart);
    const atcButtonText = this.querySelector(this.selectors.addToCartText);
    const buyNowButton = this.querySelector(this.selectors.buyNowButton);
    const comingSoonDiv = this.querySelector(this.selectors.comingSoon);
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);

    if (this.currentVariant?.available) {
      if (atcButton) {
        atcButton.removeAttribute('disabled');
      }

      if (atcButtonText) {
        atcButtonText.innerHTML = Shopify.strings.addToCart;
      }
    } else {
      if (atcButton) {
        atcButton.setAttribute('disabled', 'disabled');
      }

      if (atcButtonText) {
        atcButtonText.innerHTML = Shopify.strings.soldOut;
      }

      if (buyNowButton) {
        buyNowButton.innerHTML = Shopify.strings.soldOut;
      }

      if (comingSoonDiv) {
        comingSoonDiv.style.display = 'none';
      }

      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.style.display = 'none';
      }
    }
  }

  updateProductGiftPage() {
    const comingSoonDiv = this.querySelector(this.selectors.comingSoon);
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);

    if (this.product.gift_card || this.product.tags.includes('GIFT')) {
      if (comingSoonDiv) {
        comingSoonDiv.style.display = 'none';
      }

      if (productShipsFromDiv) {
        productShipsFromDiv.style.display = 'none';
      }

      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.style.display = 'none';
      }

      this.updateDynamicButtonText('BUY NOW');
    }
  }

  updateProductDropPage() {
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);

    if (this.product.tags.includes('DROP')) {
      const content = '<span>Third-party seller</span><span>Free shipping only for the US</span>';

      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.innerHTML = content;
      }

      if (productShipsFromDiv) {
        productShipsFromDiv.style.display = 'none';
      }
    }
  }

  changePurchaseStyle() {
    const atcButton = this.querySelector(this.selectors.addToCart);
    const productPurchaseDiv = this.querySelector(this.selectors.productPurchase);
    const productPurchaseOffset = productPurchaseDiv
      ? productPurchaseDiv.offsetTop + window.innerHeight + productPurchaseDiv.offsetHeight
      : 0;
    this.scrollPrev = 0;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (!atcButton) {
      return;
    }

    if (window.innerWidth < 750) {
      if (window.innerHeight > 499) {
        const scrolled = scrollTop + window.innerHeight;

        if (scrolled > productPurchaseOffset && scrolled > this.scrollPrev) {
          atcButton.classList.add('fixed');
        } else {
          atcButton.classList.remove('fixed');
        }

        this.scrollPrev = scrolled;
      } else {
        atcButton.classList.remove('fixed');
      }
    } else {
      atcButton.classList.remove('fixed');
    }
  }

  initSlider() {
    const thumbnailSliderElement = this.querySelector(this.selectors.thumbnailSlider);
    const prev = this.querySelector(this.selectors.thumbnailPrev);
    const next = this.querySelector(this.selectors.thumbnailNext);
    const bullets = this.querySelector(this.selectors.thumbnailDots);
    const scrollbar = this.querySelector(this.selectors.thumbnailScrollbar);

    if (!thumbnailSliderElement) return;

    new Swiper(thumbnailSliderElement, {
      speed: 500,
      slidesPerView: 1,
      spaceBetween: 5,
      watchOverflow: true,
      observer: true,
      breakpoints: {
        750: {
          slidesPerView: 4.5,
        },
      },
      navigation: {
        nextEl: next,
        prevEl: prev,
      },
      pagination: {
        el: bullets,
        type: 'bullets',
        clickable: true,
      },
      scrollbar: {
        el: scrollbar,
        draggable: true,
      },
      on: {
        afterInit: this.updateThumbnailDotForVideo.bind(this),
        observerUpdate: this.updateThumbnailDotForVideo.bind(this),
      },
    });
  }

  updateThumbnailDotForVideo(swiper) {
    const videoIndexList = [];

    swiper.slides.forEach((slide, index) => {
      if (slide.classList.contains('is-video')) {
        videoIndexList.push(index);
      }
    });

    const bullets = this.querySelectorAll(this.selectors.thumbnailDot);

    videoIndexList.forEach(index => {
      bullets[index]?.classList.add('is-video');
    });
  }

  updateImagesList() {
    const thumbnailSliderWrapperElement = this.querySelector(this.selectors.thumbnailSliderWrapper);

    if (!thumbnailSliderWrapperElement || this.allImages.length === 0) return;

    thumbnailSliderWrapperElement.innerHTML = '';

    const images = Array.from(this.allImages).filter(image => {
      const alt = image.dataset.alt;

      if (alt?.includes(this.currentVariant.handle)) {
        return true;
      }

      const variantExists = this.variants.some(({ handle }) => alt?.includes(handle));
      return !variantExists;
    });

    if (!images.length) {
      images.push(this.allImages[0]);
    }

    images.forEach(image => {
      thumbnailSliderWrapperElement.append(image);
    });

    const src = images[0].querySelector('img').getAttribute('srcset');
    const imageId = images[0].querySelector('img').dataset.imageId;
    const isVideo = !!images[0].querySelector('img').dataset.externalId;

    this.changeImage(src, imageId, isVideo);

    const thumbnailContainer = this.querySelector(this.selectors.thumbnailContainer);
    thumbnailContainer.classList.toggle('desktop-hidden', images.length <= 1);

    const bullets = this.querySelector(this.selectors.thumbnailDots);
    bullets?.setAttribute('style', 'display: flex;');
  }

  changeImage(src, imageId, isVideo) {
    const imageWrap = this.querySelector(this.selectors.productFeaturedImage);
    const image = imageWrap.querySelector('img');

    imageWrap.classList.toggle('is-video', isVideo);
    image.setAttribute('srcset', src);
    image.setAttribute('src', src);
    image.dataset.imageId = imageId;

    this.resetZoom();
  }

  changeProductDescriptionAndBadges() {
    fetch(`${location.origin}${location.pathname}?variant=${this.currentVariant.id}&section_id=main-product`)
      .then(response => response.text())
      .then(html => {
        const dom = new DOMParser().parseFromString(html, 'text/html');

        const description = dom.querySelector(this.selectors.productDescription).innerHTML;
        const $description = this.querySelector(this.selectors.productDescription);
        const $defaultDescription = this.querySelector(this.selectors.productDefaultDescription);

        if ($description && $defaultDescription) {
          $defaultDescription.style.display = description.trim() ? 'none' : 'block';
          $description.innerHTML = description;
          this.variantSpecInit();
        }

        const badges = dom.querySelector('.product__image-badges')?.innerHTML;
        const $badgeContainer = this.querySelector('.product__image-badges');

        if ($badgeContainer) {
          $badgeContainer.innerHTML = badges;
        }

        this.productNavActiveTab();
      });
  }

  updateProductProps() {
    const voltageSelect = this.querySelector(this.selectors.voltageSelect);

    if (!voltageSelect) return;

    const skuValue = voltageSelect.dataset.sku;
    const plugValue = voltageSelect.dataset.plug;
    const temp = `SKU: ${skuValue} - Plug: ${plugValue}`;
    const productPlugSKUCartNoteInput = this.querySelector(this.selectors.productPlugSKUCartNote);
    const propPlugInput = this.querySelector(this.selectors.propPlug);

    if (productPlugSKUCartNoteInput) {
      productPlugSKUCartNoteInput.value = temp;
    }

    if (propPlugInput) {
      propPlugInput.value = temp;
    }
  }

  updatePlugList() {
    const voltageSelect = this.querySelector(this.selectors.voltageSelect);
    const selectedPlugValue = voltageSelect?.dataset.plug;

    const plugs = this.currentVariant.plugs;
    let templatePlugList = '';

    const findPlug =
      plugs.find(plug => plug === selectedPlugValue) ||
      plugs.find(plug => Shopify.currentCountry && plug.trim() === Shopify.currentCountry.plug) ||
      plugs[0];

    const plugTypeMap = {
      pro: 'plug-pro',
      '(b)': 'plug-b',
      '(e)': 'plug-e',
      '(g)': 'plug-g',
      '(i)': 'plug-i',
    };

    plugs
      .filter(plug => plug)
      .forEach(plug => {
        const imgName = Object.entries(plugTypeMap).reduce((name, [key, value]) => {
          return plug.toLowerCase().includes(key) ? value : name;
        }, 'no-plug');

        templatePlugList += `<li data-plug-value="${plug}" ${plug === findPlug ? 'class="active"' : ''}>
        <img src="//cdn.shopify.com/s/files/1/2493/3652/files/${imgName}_68x68@2x.png" alt="" class="swatch-variant-img">
         <span>${plug}</span> 
      </li>`;
      });

    const plugList = document.querySelector(this.selectors.plugList);
    if (plugList) {
      plugList.innerHTML = templatePlugList;
    }

    if (voltageSelect) {
      voltageSelect.dataset.plug = findPlug;
    }

    const voltageSelectP = this.querySelector('.option-voltage-select-p');
    if (voltageSelectP) {
      voltageSelectP.innerHTML = findPlug;
    }

    this.updateProductProps();
  }

  variantSpecInit() {
    if (this.querySelector('#specs')) {
      this.querySelector('[href="#specs"]').closest('li').style.display = 'flex';
    }
  }

  productNavSticky() {
    const $productNav = document.querySelector(this.selectors.productNavContainer);
    const $header = document.querySelector(this.selectors.header);

    if (!$productNav || !$header) return;

    const onScroll = () => {
      const headerBottom = $header.getBoundingClientRect().bottom;
      const productNavTop = $productNav.getBoundingClientRect().top;

      if (headerBottom >= productNavTop) {
        $productNav.classList.add('is-sticky');
      } else {
        $productNav.classList.remove('is-sticky');
      }
    };

    const throttledScroll = throttle(onScroll, 100);
    window.addEventListener('scroll', throttledScroll);

    $productNav.addEventListener('click', evt => {
      const href = evt.target.getAttribute('href');

      if (!href || !href.includes('#')) return;

      evt.preventDefault();

      const targetElement = this.querySelector(href);

      if (targetElement) {
        window.scrollTo({
          top: targetElement.offsetTop - 155,
          behavior: 'smooth',
        });
      }
    });
  }

  productNavActiveTab() {
    const sections = document.querySelectorAll('#description, #specs, #reviews');

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('id');
            this.updateProductNavActiveTab(sectionId);
          }
        });
      },
      {
        threshold: 0.1,
      },
    );

    sections.forEach(section => {
      observer.observe(section);
    });
  }

  updateProductNavActiveTab(id) {
    const $productNav = document.querySelector(this.selectors.productNavContainer);

    if (!$productNav) return;

    const links = $productNav.querySelectorAll('a');
    links.forEach(link => {
      const targetId = link.getAttribute('href').replace('#', '');
      if (targetId === id) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  initZoom() {
    const options = { width: 520 };
    this.imageZoom = new ImageZoom(this.querySelector('.product__image'), options);
  }

  resetZoom() {
    if (this.imageZoom) {
      this.imageZoom.kill();
      this.initZoom();
    }
  }

  productNotifyObserver() {
    const target = document.querySelector(this.selectors.productNotify);
    const config = {
      attributes: false,
      childList: true,
      subtree: true,
    };
    const callback = function (mutationsList, observer) {
      const message = target.querySelector('.zooomystockmsg');
      if (message && message.innerHTML.includes('Thank you!')) {
        observer.disconnect();
        target.classList.add('sent');
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(target, config);
  }

  openGallery(activeImageId) {
    if (typeof productGalleryOpen !== 'function') {
      return;
    }

    const title = this.querySelector(this.selectors.productTitle).innerHTML;
    const thumbnails = this.querySelectorAll(this.selectors.thumbnail);
    const images = Array.from(thumbnails).map(el => {
      return {
        src: el.getAttribute('srcset'),
        alt: el.getAttribute('alt'),
        videoSrc: el.dataset.externalId ? `http://www.youtube.com/embed/${el.dataset.externalId}?enablejsapi=1` : null,
        thumbnailSrc: el.dataset.thumbnailSrc,
        active: el.dataset.imageId === activeImageId,
      };
    });

    productGalleryOpen({ images, title });
  }
}

customElements.define('main-product', MainProduct);
